
import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/use-toast';

interface JiraCompanyNameDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onCompanyNameSubmit: (companyName: string) => void;
}

const JiraCompanyNameDialog = ({ 
  isOpen, 
  onOpenChange, 
  onCompanyNameSubmit 
}: JiraCompanyNameDialogProps) => {
  const [companyName, setCompanyName] = useState('');

  useEffect(() => {
    if (isOpen) {
      // Load previously saved company name from localStorage
      const savedCompanyName = localStorage.getItem('jira-company-name');
      if (savedCompanyName) {
        setCompanyName(savedCompanyName);
      }
    }
  }, [isOpen]);

  const handleSubmit = () => {
    if (!companyName.trim()) {
      toast({
        title: "Company Name Required",
        description: "Please enter a company name to generate Jira User IDs",
        variant: "destructive"
      });
      return;
    }

    // Save company name for future use
    localStorage.setItem('jira-company-name', companyName.trim());
    
    onCompanyNameSubmit(companyName.trim());
    onOpenChange(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Generate Jira User IDs</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Enter your company name to automatically generate Jira User IDs for all resources in the format: firstname.lastname@companyname.com
          </p>
          <div className="space-y-2">
            <Label htmlFor="company-name">Company Name</Label>
            <Input
              id="company-name"
              placeholder="e.g., Ingant, TechCorp, etc."
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
            />
          </div>
          <div className="text-xs text-gray-500">
            Example: "James Clark" will become "james.clark@{companyName}.com"
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit}>
              Generate User IDs
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default JiraCompanyNameDialog;
